import { formatDimensionValue } from "@/lib/formatDimensionValue";

import type {
  FilterOperator,
  FilterProperty,
  FilterPropertyKind,
  PropertyFilterQuery,
  PropertyFilterToken,
} from "./filterTypes";
import {
  DEFAULT_OPERATORS,
  EMPTY_PROPERTY_FILTER_QUERY,
  OPERATOR_LABELS,
  operatorLabel,
} from "./filterTypes";

/**
 * Window event dispatched after Kai-applied filters are written to
 * sessionStorage, so a mounted table can refresh its in-memory query.
 */
export const KAI_TABLE_FILTERS_EVENT = "kai-table-filters";

export function isFilterOperator(value: string): value is FilterOperator {
  return value in OPERATOR_LABELS;
}

export function newTokenId(): string {
  return `flt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function operatorsFor(property: FilterProperty): FilterOperator[] {
  return [...(property.operators ?? DEFAULT_OPERATORS[property.kind])];
}

export function defaultOperatorFor(property: FilterProperty): FilterOperator {
  return property.defaultOperator ?? operatorsFor(property)[0] ?? ":";
}

/** Whether one filter chip can hold several values (OR within the property). */
export function propertySupportsMultipleValues(
  property: FilterProperty,
): boolean {
  if (property.allowMultiple === false) return false;
  if (property.allowMultiple === true) return true;
  if (property.kind === "date" || property.kind === "number") return false;
  if (property.kind === "enum") return true;
  if (property.kind === "text" && (property.loadOptions || property.options)) {
    return true;
  }
  return false;
}

export function splitTokenValues(raw: string): string[] {
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function joinTokenValues(values: readonly string[]): string {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))].join(",");
}

function labelForValue(
  property: FilterProperty | undefined,
  value: string,
): string {
  return (
    property?.options?.find((option) => option.value === value)?.label ?? value
  );
}

export function formatTokenValueDisplay(
  propertyKey: string,
  property: FilterProperty | undefined,
  value: string,
  valueLabel?: string,
): string {
  const values = splitTokenValues(value);
  if (values.length <= 1) {
    const raw = valueLabel ?? value;
    return formatDimensionValue(propertyKey, raw);
  }
  const labelParts = valueLabel
    ? valueLabel.split(",").map((part) => part.trim())
    : values.map((part) => labelForValue(property, part));
  return values
    .map((part, index) =>
      formatDimensionValue(propertyKey, labelParts[index] ?? part),
    )
    .join(", ");
}

export function mergePropertyFilterTokens(
  existing: PropertyFilterToken,
  incoming: PropertyFilterToken,
  property: FilterProperty,
): PropertyFilterToken {
  const values = joinTokenValues([
    ...splitTokenValues(existing.value),
    ...splitTokenValues(incoming.value),
  ]);
  const labelByValue = new Map<string, string>();
  for (const token of [existing, incoming]) {
    const parts = splitTokenValues(token.value);
    const labels = token.valueLabel
      ? token.valueLabel.split(",").map((part) => part.trim())
      : parts;
    parts.forEach((part, index) => {
      labelByValue.set(part, labels[index] ?? labelForValue(property, part));
    });
  }
  const valueLabel = splitTokenValues(values)
    .map((part) => labelByValue.get(part) ?? labelForValue(property, part))
    .join(", ");
  return { ...existing, value: values, valueLabel };
}

/** Add or merge a token; multi-value properties coalesce on property + operator. */
export function upsertPropertyFilterToken(
  tokens: readonly PropertyFilterToken[],
  incoming: PropertyFilterToken,
  properties: readonly FilterProperty[],
): PropertyFilterToken[] {
  if (!incoming.value.trim()) return [...tokens];

  const property = incoming.propertyKey
    ? properties.find((item) => item.key === incoming.propertyKey)
    : undefined;

  if (!property || !propertySupportsMultipleValues(property)) {
    const duplicate = tokens.some(
      (token) =>
        (token.propertyKey ?? "search") === (incoming.propertyKey ?? "search") &&
        token.operator === incoming.operator &&
        token.value === incoming.value,
    );
    return duplicate ? [...tokens] : [...tokens, incoming];
  }

  const index = tokens.findIndex(
    (token) =>
      token.propertyKey === incoming.propertyKey &&
      token.operator === incoming.operator,
  );
  if (index < 0) return [...tokens, incoming];

  const merged = mergePropertyFilterTokens(tokens[index], incoming, property);
  return tokens.map((token, i) => (i === index ? merged : token));
}

export function consolidateMultiValueTokens(
  query: PropertyFilterQuery,
  properties: readonly FilterProperty[],
): PropertyFilterQuery {
  let tokens: PropertyFilterToken[] = [];
  for (const token of query.tokens) {
    tokens = upsertPropertyFilterToken(tokens, token, properties);
  }
  return { ...query, tokens };
}

export function tokenLabel(
  token: PropertyFilterToken,
  properties: readonly FilterProperty[],
): string {
  if (!token.propertyKey) return token.valueLabel ?? token.value;
  const property = properties.find((item) => item.key === token.propertyKey);
  const name = property?.label ?? token.propertyKey;
  const operator = operatorLabel(token.operator, property?.kind);
  const displayValue = formatTokenValueDisplay(
    token.propertyKey,
    property,
    token.value,
    token.valueLabel,
  );
  return `${name} ${operator} ${displayValue}`;
}

export function hasPropertyFilters(query: PropertyFilterQuery): boolean {
  return query.tokens.some((token) => token.value.trim() !== "");
}

export function serializePropertyFilterQuery(query: PropertyFilterQuery): string {
  return JSON.stringify({
    o: query.operation,
    t: query.tokens.map((token) => ({
      k: token.propertyKey,
      op: token.operator,
      v: token.value,
      l: token.valueLabel,
    })),
  });
}

export function deserializePropertyFilterQuery(
  raw: string | null,
): PropertyFilterQuery {
  if (!raw) return EMPTY_PROPERTY_FILTER_QUERY;
  try {
    const parsed = JSON.parse(raw) as {
      o?: "and" | "or";
      t?: { k?: string; op?: FilterOperator; v?: string; l?: string }[];
    };
    const tokens = (parsed.t ?? [])
      .filter((item) => typeof item.v === "string" && item.v.trim() !== "")
      .map((item) => ({
        id: newTokenId(),
        propertyKey: item.k || undefined,
        operator: item.op ?? ":",
        value: item.v ?? "",
        valueLabel: item.l,
      }));
    return {
      operation: parsed.o === "or" ? "or" : "and",
      tokens,
    };
  } catch {
    return EMPTY_PROPERTY_FILTER_QUERY;
  }
}

export interface PropertyFilterParamMaps {
  dates?: Record<string, Partial<Record<FilterOperator, string>>>;
  numbers?: Record<string, Partial<Record<FilterOperator, string>>>;
}

/**
 * Flatten a filter query into API-style string params.
 * Text/enum keys accumulate with commas; negation uses `{key}_not`.
 */
export function queryToFilterParams(
  query: PropertyFilterQuery,
  maps: PropertyFilterParamMaps = {},
): Record<string, string> {
  const filters: Record<string, string> = {};

  const accumulate = (key: string, value: string) => {
    filters[key] = filters[key] ? `${filters[key]},${value}` : value;
  };

  for (const token of query.tokens) {
    const value = token.value.trim();
    if (!value) continue;

    if (!token.propertyKey || token.propertyKey === "search") {
      filters.search = filters.search ? `${filters.search} ${value}` : value;
      continue;
    }

    const dateMap = maps.dates?.[token.propertyKey];
    if (dateMap) {
      if (token.operator === "=") {
        const fromKey = dateMap[">="];
        const toKey = dateMap["<="];
        if (fromKey) filters[fromKey] = value;
        if (toKey) filters[toKey] = value;
      } else {
        const apiKey = dateMap[token.operator];
        if (apiKey) filters[apiKey] = value;
      }
      continue;
    }

    const numberMap = maps.numbers?.[token.propertyKey];
    if (numberMap) {
      const apiKey = numberMap[token.operator] ?? numberMap["="];
      if (apiKey) filters[apiKey] = value;
      continue;
    }

    const negated = token.operator === "!:" || token.operator === "!=";
    const apiKey = negated
      ? `${token.propertyKey}_not`
      : token.propertyKey;
    for (const part of splitTokenValues(value)) {
      accumulate(apiKey, part);
    }
  }

  if (query.operation === "or") {
    filters.filter_operation = "or";
  }

  return filters;
}

function fieldMatches(
  field: string,
  operator: FilterOperator,
  value: string,
  kind: FilterPropertyKind,
): boolean {
  if (kind === "date" || kind === "number") {
    const left = field;
    const right = value;
    if (operator === "=") return left === right;
    if (operator === "!=") return left !== right;
    if (operator === ">=") return left >= right;
    if (operator === "<=") return left <= right;
    if (operator === ">") return left > right;
    if (operator === "<") return left < right;
  }

  const haystack = field.toLowerCase();
  const needle = value.toLowerCase();
  if (operator === ":") return haystack.includes(needle);
  if (operator === "!:") return !haystack.includes(needle);
  if (operator === "=") return haystack === needle;
  if (operator === "!=") return haystack !== needle;
  return haystack.includes(needle);
}

/** Client-side matcher for mock / in-memory tables. */
export function rowMatchesPropertyQuery<T>(
  row: T,
  query: PropertyFilterQuery,
  getField: (row: T, propertyKey: string) => string,
  properties: readonly FilterProperty[],
): boolean {
  const tokens = query.tokens.filter((token) => token.value.trim() !== "");
  if (tokens.length === 0) return true;

  const matches = tokens.map((token) => {
    if (!token.propertyKey) {
      const haystack = getField(row, "search").toLowerCase();
      return haystack.includes(token.value.trim().toLowerCase());
    }
    const property = properties.find((item) => item.key === token.propertyKey);
    const kind = property?.kind ?? "text";
    const field = getField(row, token.propertyKey);
    const parts = splitTokenValues(token.value);
    const negated = token.operator === "!:" || token.operator === "!=";
    if (parts.length <= 1) {
      return fieldMatches(field, token.operator, token.value, kind);
    }
    if (negated) {
      return parts.every((part) => fieldMatches(field, token.operator, part, kind));
    }
    return parts.some((part) => fieldMatches(field, token.operator, part, kind));
  });

  return query.operation === "or" ? matches.some(Boolean) : matches.every(Boolean);
}
